import {
  faEye,
  faPenToSquare,
  faPlus,
} from "@fortawesome/pro-duotone-svg-icons";
import { faArrowUpFromBracket } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  Card,
  Col,
  Divider,
  Flex,
  Form,
  Grid,
  Input,
  InputNumber,
  message,
  Popconfirm,
  Row,
  Select,
  Skeleton,
  Space,
  Switch,
  Table,
  Tabs,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import { useLocation } from "react-router-dom";
import CustomDateTimePicker from "../common/CustomDateTimePicker";
import DocumentModal from "../components/DocumentModal";
import DocumentPreviewModal from "../components/DocumentPreviewModal";
import { useAuth } from "../config/AuthProvider";
import { useGoBack } from "../customHooks/useGoBack";
import {
  ContainerDeliveryMethodSelect,
  ContainerLoadTypeSelect,
  ForwarderSelect,
  SupplierOrgSelect,
  validateNonNegativeNumber,
} from "../helpers/common";
import dayjs, { fromUtc, toUtcISOString } from "../helpers/dayjsSetup";
import ContainerService from "../services/Container/ContainerService";
import DocumentService from "../services/Document/DocumentService";
import ShipmentService from "../services/Shipment/ShipmentService";
import WarehouseSelect from "../components/WarehouseSelect";
import WarehouseReceivingZoneSelect from "../components/WarehouseReceivingZoneSelect";
import WarehouseDockSelect, { warehouseDockValidator } from "../components/WarehouseDockSelect";
import { warehouseDeliveryValidator } from "../components/kanbanDashboard/WarehouseDetailSelection";
import useWarehouseReceivingZone from "../customHooks/useWarehouseReceivingZone";
import { InvalidateDashboardQueries } from "../components/kanbanDashboard/DashboardCacheCleaner";
import { queryClient } from "../react-query-client";
import AppButton from "../common/AppButton";
import ConfirmDeleteButton from "../common/actions/ConfirmDeleteButton";

const { Text } = Typography;
const { TextArea } = Input;

export default function ShipmentDetailsNew() {
  const location = useLocation();
  const goBack = useGoBack();
  const containerID = location?.state?.containerID;
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(true);
  const [isPaid, setIsPaid] = useState(false);
  const [isDeliveryConfirmed, setIsDeliveryConfirmed] = useState(false);
  const [isStockReceived, setIsStockReceived] = useState(false);
  const [isOnSite, setIsOnSite] = useState(false);
  const [isAlertActive, setIsAlertActive] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [allDocTypes, setAllDocTypes] = useState([]);
  const [docList, setDocList] = useState([]);
  // const [refresh, setRefresh] = useState(0);
  // const [selectedDocTypeID, setSelectedDocTypeID] = useState(null);
  const [selectedDocRow, setSelectedDocRow] = useState(null);
  const { user } = useAuth();
  const [activeKey, setActiveKey] = useState("document");
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [isShipmentLCL, setIsShipmentLCL] = useState(false);
  const [isLiveUnload, setIsLiveUnload] = useState(false);

  const [form] = Form.useForm();

  useEffect(() => {
    fetchContainerDetails(containerID);
    fetchDocumentTypes();
    window.scrollTo(0, 0);
  }, []);

  const fetchDocumentTypes = () => {
    DocumentService.getAllDocumentTypes()
      .then((response) => {
        if (response.status === 200) {
          setAllDocTypes(response.data);
        }
      })
      .catch((error) => {
        console.error("Error fetching document types:", error);
      });
  };

  useEffect(() => {
    if (record?.shipment && record?.container) {
      form.setFields([
        { name: "containerNum", value: record.container.containerNum },
        { name: "type", value: record.container.type },
        { name: "userReference", value: record.container.userReference },
        { name: "statusName", value: record.statusName },
        { name: "isOnSiteEnabled", value: record.isOnSiteEnabled },
        { name: "supplierID", value: record.supplier.id },
        { name: "forwarderID", value: record.shipment.forwarderID },
        {
          name: "containerLoadType",
          value: record.shipment.containerLoadType,
        },
        // { name: "cargoReadyDate", value: fromUtc(record.shipment.cargoReadyDate) },
        { name: "portOfLoadingCode", value: record.shipment.portOfLoadingCode },
        { name: "portOfLoading", value: record.shipment.portOfLoading },

        {
          name: "portOfDischargeCode",
          value: record.shipment.portOfDischargeCode,
        },
        {
          name: "portOfDischarge",
          value: record.shipment.portOfDischarge,
        },
        { name: "sealineCode", value: record.shipment.sealineCode },
        { name: "sealineName", value: record.shipment.sealineName },
        {
          name: "eta",
          value: record.shipment.eta ? dayjs(record.shipment.eta) : null,
        },
        {
          name: "etd",
          value: record.shipment.etd ? dayjs(record.shipment.etd) : null,
        },
        { name: "vesselName", value: record.shipment.vesselName },
        { name: "imoNum", value: record.shipment.imoNum },
        { name: "mblNum", value: record.shipment.mblNum },
        { name: "voyageNum", value: record.shipment.voyageNum },
        {
          name: "paymentDueDate",
          value: record.container.paymentDueDate
            ? dayjs(record.container.paymentDueDate)
            : null,
        },
        { name: "isPaid", value: isPaid },
        { name: "isDeliveryConfirmed", value: isDeliveryConfirmed },
        {
          name: "deliveryDate",
          value: record.container.deliveryDate
            ? dayjs(record.container.deliveryDate)
            : null,
        },
        {
          name: "containerDeliveryMethodID",
          value: record.container.containerDeliveryMethodID,
        },
        { name: "boxes", value: record.container.boxes },
        {
          name: "boxesUnloadManHours",
          value: record.container.boxesUnloadManHours,
        },
        {
          name: "boxesActualUnloadHour",
          value: record.container.boxesActualUnloadHour,
        },
        { name: "pallets", value: record.container.pallets },
        {
          name: "palletsUnloadManHours",
          value: record.container.palletsUnloadManHours,
        },
        {
          name: "palletsActualUnloadHour",
          value: record.container.palletsActualUnloadHour,
        },
        { name: "isStockReceived", value: isStockReceived },
        { name: "isOnSite", value: isOnSite },
        {
          name: "unloadComplete",
          value: record.container.unloadComplete
            ? dayjs(record.container.unloadComplete)
            : null,
        },
        {
          name: "collectionBooked",
          value: record.container.collectionBooked
            ? dayjs(record.container.collectionBooked)
            : null,
        },
        {
          name: "containerCollected",
          value: record.container.containerCollected
            ? dayjs(record.container.containerCollected)
            : null,
        },
        {
          name: "containerReturned",
          value: record.container.containerReturned
            ? dayjs(record.container.containerReturned)
            : null,
        },
        { name: "contents", value: record.container.contents },
        { name: "notes", value: record.container.notes },
        { name: "isAlertActive", value: isAlertActive },
        { name: "alertDate", value: fromUtc(record.container.alertDate) },
        { name: "alertNote", value: record.container.alertNote },
        { name: "alertUser", value: record.container.alertUserName },
        {
          name: "detentionFreeDays",
          value: record.shipment.detentionFreeDays,
        },
      ]);
    }
  }, [record]);

  const fetchContainerDetails = (id) => {
    setLoading(true);
    ContainerService.getContainerDetails(id)
      .then((res) => {
        const data = res.data;

        setRecord(data);
        setIsPaid(data?.container?.isPaid);
        setIsDeliveryConfirmed(data?.container?.isDeliveryConfirmed);
        setIsStockReceived(data?.container?.isStockReceived);
        setIsOnSite(data?.container?.isOnSite);
        setIsAlertActive(data?.container?.isAlertActive);
        setIsLiveUnload(data?.container?.containerDeliveryMethodID === 1);
        setIsShipmentLCL(data?.shipment?.containerLoadType === "LCL");
        form.setFieldsValue({
          ...data.container,
          eta: dayjs(data?.shipment?.eta),
          etd: dayjs(data?.shipment?.etd),
          unloadComplete: dayjs(data?.container?.unloadComplete),
          collectionBooked: dayjs(data?.container?.collectionBooked),
          containerCollected: dayjs(data?.container?.containerCollected),
          containerReturned: dayjs(data?.container?.containerReturned),
          paymentDueDate: dayjs(data?.container?.paymentDueDate),
          deliveryDate: dayjs(data?.container?.deliveryDate),
          mblNum: data.shipment?.mblNum,
          alertDate: fromUtc(data?.container?.alertDate),
          warehouseId: data?.warehouse?.warehouse?.id,
          warehouseReceivingZoneId: data?.warehouse?.warehouseZone?.id,
          warehouseDockId: data?.warehouse?.warehouseDock?.id,
        });
        setLoading(false);
      })
      .catch((e) => {
        console.log(e);
        message.error("Failed to fetch container details");
        setLoading(false);
      });
  };

  const handleClose = () => {
    goBack();
  };

  const uploadModal = () => {
    setIsModalOpen(true);
  };

  const selectedWarehouseId = Form.useWatch("warehouseId", form);
  const selectedReceivingZoneId = Form.useWatch(
    "warehouseReceivingZoneId",
    form,
  );

  const { data: receivingZones } = useWarehouseReceivingZone(selectedWarehouseId);

  const handleWarehouseChange = (warehouseId) => {
    if (selectedWarehouseId !== warehouseId) {
      form.setFieldsValue({
        warehouseReceivingZoneId: null,
        warehouseDockId: null,
      });
    }
  };

  const handleReceivingZoneChange = (receivingZoneId) => {
    if (selectedReceivingZoneId !== receivingZoneId) {
      form.setFieldsValue({ warehouseDockId: null });
    }
  };

  useEffect(() => {
    if (allDocTypes.length && record?.documents) {
      const merged = allDocTypes.map((type) => {
        const existingDoc = record.documents.find(
          (d) => d.documentTypeId === type.id,
        );
        return {
          key: type.id,
          documentTypeID: type.id,
          typeName: type.type,
          isMandatory: type.isMandatory,
          id: existingDoc?.id,
          fileName: existingDoc?.originalFileName || "",
          notes: existingDoc?.notes || "",
          uploaded: !!existingDoc,
        };
      });
      setDocList(merged);
    }
  }, [allDocTypes, record]);

  const handleChange = (index, field, value) => {
    const updated = [...docList];
    updated[index][field] = value;
    setDocList(updated);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedDocRow(null);
    setSelectedIndex(null);
  };

  const fetchLatestDetails = () => {
    ShipmentService.fetchTrackingDetails(record?.container?.containerNum);
    message.success("Shipment details updated successfully");
    setIsEditing(false);
  };

  const handleDelete = () => {
    ShipmentService.deleteShipment(containerID)
      .then((response) => {
        if (response.status === 200) {
          message.success("Shipment Deleted Successfully");
          goBack();
        }
      })
      .catch((error) => {
        message.error("Error deleting shipment. " + error.message);
      });
    setIsEditing(false);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

       if (values.deliveryDate && (!values.warehouseId || !values.warehouseReceivingZoneId)) {
        message.error(
          "Warehouse and Receiving Zone details have not been populated. Please select the appropriate Warehouse and Receiving Zone."
        );
        return;
      }

      const request = {
        containerNum: values.containerNum,
        type: values.type,
        userReference: values.userReference,
        supplierId: values.supplierID,
        forwarderId: values.forwarderID,
        // cargoReadyDate: toUtcISOString(values.cargoReadyDate),
        portOfLoadingCode: values.portOfLoadingCode,
        portOfDischargeCode: values.portOfDischargeCode,
        eta: values.eta?.format("YYYY-MM-DDTHH:mm"),
        vesselName: values.vesselName,
        imoNum: values.imoNum,
        mblNum: values.mblNum,
        voyageNum: values.voyageNum,
        paymentDueDate: values.paymentDueDate?.format("YYYY-MM-DDTHH:mm"),
        isPaid: isPaid,
        isDeliveryConfirmed: isDeliveryConfirmed,
        deliveryDate: values.deliveryDate?.format("YYYY-MM-DDTHH:mm"),
        containerDeliveryMethodId: values.containerDeliveryMethodID,
        boxes: values.boxes,
        boxesUnloadManHours: values.boxesUnloadManHours,
        boxesActualUnloadHour: values.boxesActualUnloadHour,
        pallets: values.pallets,
        palletsUnloadManHours: values.palletsUnloadManHours,
        palletsActualUnloadHour: values.palletsActualUnloadHour,
        isStockReceived: isStockReceived,
        isOnSite: isOnSite,
        unloadComplete: values.unloadComplete?.format("YYYY-MM-DDTHH:mm"),
        collectionBooked: values.collectionBooked?.format("YYYY-MM-DDTHH:mm"),
        containerCollected:
          values.containerCollected?.format("YYYY-MM-DDTHH:mm"),
        containerReturned: values.containerReturned?.format("YYYY-MM-DDTHH:mm"),
        contents: values.contents,
        notes: values.notes,
        isAlertActive: isAlertActive,
        alertDate: toUtcISOString(values.alertDate),
        alertNote: values.alertNote,
        containerLoadType: values.containerLoadType,
        warehouseId: values.warehouseId,
        warehouseReceivingZoneId: values.warehouseReceivingZoneId,
        warehouseDockId: values.warehouseDockId,
        detentionFreeDays: values.detentionFreeDays,
      };
      await ShipmentService.updateShipmentDetails(containerID, request);
      message.success("Shipment details updated successfully");
      setIsEditing(false);
      InvalidateDashboardQueries(queryClient);
      goBack();
    } catch (error) {
      if (error.response && error.response.data) {
        message.error(error.response.data.message);
      } else {
        message.error(error.message || "Unexpected error occurred");
      }
    }
  };

  const handleSelectedDocument = (row, index) => {
    setSelectedIndex(index);
    setSelectedDocRow(row);
    setIsModalOpen(true);
  };

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const handlePreview = (record) => {
    setSelectedDocument(record);
    setIsModalVisible(true);
  };

  const columns = [
    {
      title: "",
      key: "preview",
      align: "center",
      render: (row, index) => (
        <Button
          type="link"
          // onClick={() => handleSelectedDocument(row, index)}
          onClick={() => handlePreview(row)}
        >
          {/* default */}
          <FontAwesomeIcon
            icon={faEye}
            className="text-2xl text-black block p-2 hover:text-cyan-700"
          />
        </Button>
      ),
    },
    {
      title: <span className="ml-10">Type</span>,
      // dataIndex: ["documentType", "type"],
      render: (_, record) => {
        const isMandatory = record.documentType?.isMandatory;
        const hasUser = !!record.user;
        return (
          <span className="flex items-center justify-start gap-4">
            {isMandatory && hasUser && (
              <span className="w-5 h-5 rounded-full bg-green-500 opacity-75" />
            )}
            {isMandatory && !hasUser && (
              <span className="w-5 h-5 rounded-full bg-red-500 opacity-75" />
            )}
            {!isMandatory && (
              <span className="w-5 h-5" /> // empty space
            )}
            <span className="text-sm">{record.documentType?.type}</span>
          </span>
        );
      },
    },
    {
      title: "File Name",
      dataIndex: "originalFileName",
      width: 600,
      render: (text, record, index) => {
        const handleDownload = async () => {
          try {
            const response = await DocumentService.handleDownload(record?.id);
            const contentDisposition = response.headers["content-disposition"];
            let fileName = response.fileName || "download";
            if (contentDisposition) {
              const utf8Match = contentDisposition.match(
                /filename\*\=UTF-8''([^;]+)/,
              );
              if (utf8Match) {
                fileName = decodeURIComponent(utf8Match[1]);
              }
            }

            const blob = new Blob([response.data]);
            const blobUrl = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
          } catch (error) {
            if (error?.status === 404) {
              message.error("Document Not Found");
            } else {
              message.error(
                "Something went wrong while downloading the document",
              );
            }
            console.error("Document Download Failed. ", error);
          }
        };

        return record.id ? (
          <div className="grid">
            <a
              onClick={handleDownload}
              className="p-0 cursor-pointer text-black text-sm"
            >
              {record.document?.OriginalFileName || record.originalFileName}
            </a>
            <span className="text-sm">
              uploaded on {fromUtc(record.createdDate).format("DD MMM YYYY")}
            </span>
          </div>
        ) : (
          <Text
            type="link"
            onClick={() => handleSelectedDocument(record, index)}
            className="hover:cursor-pointer flex items-center justify-start w-full text-[#015373] text-sm"
          >
            Upload
            <FontAwesomeIcon icon={faArrowUpFromBracket} className="ml-2" />
          </Text>
        );
      },
    },

    {
      title: "Note",
      dataIndex: "notes",
      width: 250,
      render: (text, row, index) =>
        row.isEditing ? (
          <Input
            value={text}
            onChange={(e) => handleChange(index, "notes", e.target.value)}
            className="text-sm"
          />
        ) : text ? (
          text.length > 20 ? (
            `${text.substring(0, 20)}...`
          ) : (
            text
          )
        ) : (
          "-"
        ),
    },
    {
      title: "Uploaded By",
      dataIndex: "user",
      render: (user) => (
        <span className="text-sm">
          {user?.firstName ? user.firstName + " " + user.lastName : "-"}
        </span>
      ),
    },
    {
      title: "Action",
      key: "acrion",
      align: "center",
      render: (value, row, index) => {
        const isSelected = selectedIndex === index;
        return (
          <Button
            type="link"
            onClick={() => handleSelectedDocument(row, index)}
          >
            {isSelected ? (
              <FontAwesomeIcon
                icon={faPenToSquare}
                className="text-xl block pl-3 p-2 text-cyan-700 bg-neutral-200 rounded-3xl"
              />
            ) : (
              <FontAwesomeIcon
                icon={faPenToSquare}
                className="text-xl text-black block pl-3 p-2 hover:text-cyan-500"
              />
            )}
          </Button>
        );
      },
    },
  ];

  const statusColumns = [
    { title: "Status", dataIndex: "status" },
    {
      title: "Event Date",
      dataIndex: "eventDate",
      render: (text) =>
        loading ? (
          <Skeleton active />
        ) : (
          <span className="text-sm">
            {dayjs(text).format("DD MMM YYYY HH:mm")}
          </span>
        ),
    },
    {
      title: "Update Date",
      dataIndex: "updateDate",
      render: (text) => (
        <span className="text-sm">
          {fromUtc(text).format("DD MMM YYYY HH:mm")}
        </span>
      ),
    },
    { title: "Source", dataIndex: "trackingType" },
  ];
  const etaColumns = [
    {
      title: "Update Date",
      dataIndex: "createdDate",
      render: (text) => fromUtc(text).format("DD MMM YYYY HH:mm"),
    },
    {
      title: "New ETA",
      dataIndex: "etaDate",
      render: (text) => dayjs(text).format("DD MMM YYYY HH:mm"),
    },
    {
      title: "Previous ETA",
      dataIndex: "etaDatePrevious",
      render: (text) => (text ? dayjs(text).format("DD MMM YYYY HH:mm") : "-"),
    },
    { title: "Source", dataIndex: "trackingType" },
  ];

  return (
    <div className="px-8 relative mb-4">
      <div className="flex justify-start items-center w-full my-2  mx-2 font-inter">
        <span className="!font-medium text-[30px] !text-[#015373] !m-0 pl-4 mt-10">
          Shipment Details
        </span>
        <div className=" flex items-center justify-end pr-4 pt-2 h-full">
          <Button
            type="link"
            onClick={goBack}
            className="text-md font-medium !text-[#01A4A6] "
          >
            / Shipment Gateway
          </Button>
        </div>
      </div>
      {/* <div className="absolute top-[-32px] left-[250px]">
        {record?.isManual && (
          <Popconfirm
            title="Are you sure you want to fetch latest details?"
            onConfirm={fetchLatestDetails}
            okText="Yes"
            cancelText="No"
            placement="bottom"
          >
            <Tooltip title="Re-Track Shipment" placement="right">
              <Button type="primary" className="font-bold" icon={<FontAwesomeIcon icon={faRefresh} />} />
            </Tooltip>
          </Popconfirm>
        )}
      </div> */}
      <Form form={form} layout="vertical" colon={false}>
        <div className="flex flex-col gap-2 lg:flex-row">
          <div className="lg:w-1/2 flex flex-col gap-2">
            <Card className="pb-1  rounded-3xl shadow-xl bg-white bg-opacity-80   ">
              <div className="grid grid-cols-2 gap-2 items-end">
                <Form.Item
                  label="Container Number"
                  name="containerNum"
                  rules={[
                    {
                      required: isEditing ? false : true,
                      message: "Please input the container number!",
                    },
                  ]}
                >
                  <div className="font-medium text-sm m-0 p-0">
                    {form.getFieldValue("containerNum")}
                  </div>
                </Form.Item>
                <Form.Item label="Status" name="statusName">
                  <div className="font-medium text-sm m-0 p-0">
                    {form.getFieldValue("statusName")}
                  </div>
                </Form.Item>
                <Form.Item name="etd" label="ETD">
                  <div className="font-medium text-sm">
                    {form.getFieldValue("etd")
                      ? dayjs(form.getFieldValue("etd"))
                        .format("DD MMM YYYY")
                        .toUpperCase()
                      : ""}
                  </div>
                </Form.Item>{" "}
                <Form.Item
                  name="eta"
                  label="ETA"
                  rules={[
                    { required: true, message: "Please Select ETA Date" },
                  ]}
                >
                  <CustomDateTimePicker
                    type="date"
                    form={form}
                    itemName="eta"
                    className="w-full text-[14px] h-[34px]"
                  />
                </Form.Item>
                <Form.Item label="Port of Loading" name="portOfLoadingCode">
                  <div className="font-medium text-sm flex justify-start items-center">
                    {form.getFieldValue("portOfLoadingCode") ? (
                      <>
                        <ReactCountryFlag
                          countryCode={form
                            .getFieldValue("portOfLoadingCode")
                            .substring(0, 2)}
                          svg
                          className="!w-7 !h-6"
                        />
                        <span className="ml-2">
                          {form.getFieldValue("portOfLoading")}
                        </span>{" "}
                      </>
                    ) : (
                      <div className="w-6 h-5 mr-2 flex-shrink-0"></div>
                    )}
                  </div>
                </Form.Item>
                <Form.Item label="Port of Discharge" name="portOfDischargeCode">
                  <div className="font-medium text-sm flex justify-start items-center">
                    {form.getFieldValue("portOfDischargeCode") ? (
                      <>
                        <ReactCountryFlag
                          countryCode={form
                            .getFieldValue("portOfDischargeCode")
                            .substring(0, 2)}
                          svg
                          className="!w-7 !h-6"
                        />
                        <span className="ml-2">
                          {form.getFieldValue("portOfDischarge")}
                        </span>
                      </>
                    ) : (
                      <div className="mb-8"></div>
                    )}
                  </div>
                </Form.Item>{" "}
                <Form.Item label="BL/Reference Number" name="mblNum">
                  <Input size="large" />
                </Form.Item>
                <Form.Item label="User Reference" name="userReference">
                  <Input size="large" />
                </Form.Item>
                <Form.Item
                  label="Forwarder"
                  name="forwarderID"
                  rules={[
                    { required: true, message: "Please Select Forwarder" },
                  ]}
                >
                  <ForwarderSelect
                    selectClassNames="!h-[34px] "
                    value={form.getFieldValue("forwarderID")}
                    onChange={(id) => {
                      form.setFieldsValue({ forwarderID: id });
                    }}
                    onForwarderChange={(forwarder) => {
                      form.setFieldsValue({
                        detentionFreeDays: forwarder?.detentionFreeDays ?? 0
                      });
                    }}
                  />
                </Form.Item>
                <Form.Item
                  name="detentionFreeDays"
                  label="Detention Free Days"
                >
                  <InputNumber
                    controls={false}
                    min={0}
                    onKeyDown={(e) => {
                      const allowedKeys = [
                        "Backspace",
                        "Delete",
                        "ArrowLeft",
                        "ArrowRight",
                        "Tab",
                        "Home",
                        "End",
                      ];
                      const isNumberKey = e.key >= "0" && e.key <= "9";
                      const isAllowed =
                        isNumberKey || allowedKeys.includes(e.key);

                      if (!isAllowed) {
                        e.preventDefault();
                      }
                    }}
                  />
                </Form.Item>
                <Form.Item
                  name="supplierID"
                  label="Supplier"
                  rules={[
                    { required: true, message: "Please Select Supplier" },
                  ]}
                >
                  <SupplierOrgSelect form={form} />
                </Form.Item>
                <Form.Item
                  name="containerLoadType"
                  label="Load Type"
                  rules={[
                    { required: true, message: "Please Select Container Load Type" },
                  ]}
                >
                  <ContainerLoadTypeSelect placeholder="" onChange={(value) => setIsShipmentLCL(value === "LCL")} />
                </Form.Item>
                <Form.Item label="Vessel Name" name="vesselName">
                  <div className="font-medium text-sm">
                    {form.getFieldValue("vesselName")}
                  </div>
                </Form.Item>{" "}
                <Form.Item label="Voyage Number" name="voyageNum">
                  <div className="font-medium text-sm">
                    {form.getFieldValue("voyageNum")}
                  </div>
                </Form.Item>
                <Form.Item label="Sealine Name" name="sealineName">
                  <div className="font-medium text-sm">
                    {form.getFieldValue("sealineName")}
                  </div>
                </Form.Item>
                <Form.Item label="Sealine Code" name="sealineCode">
                  <div className="font-medium text-sm">
                    {form.getFieldValue("sealineCode")}
                  </div>
                </Form.Item>
                <Form.Item
                  label="IMO Number"
                  name="imoNum"
                  style={{ marginBottom: 0 }}
                >
                  <div className="font-medium text-sm">
                    {form.getFieldValue("imoNum")}
                  </div>
                </Form.Item>
                <Form.Item label="Type" name="type" style={{ marginBottom: 0 }}>
                  <div className="font-medium text-sm">
                    {form.getFieldValue("type")}
                  </div>
                </Form.Item>
              </div>
            </Card>
            <Card className="card-form">
              <div className="grid items-end">
                <Form.Item
                  className=""
                  label="Alert Active?"
                  name="isAlertActive"
                  valuePropName="checked"
                >
                  <Switch
                    checked={isAlertActive}
                    onChange={(checked) => {
                      setIsAlertActive(checked);
                      if (checked) {
                        form.setFieldsValue({
                          alertDate: dayjs().tz(),
                          alertUser: user?.name,
                        });
                      } else {
                        form.setFieldsValue({
                          alertDate: null,
                          alertUser: null,
                          alertNote: null,
                        });
                      }
                    }}
                  />
                </Form.Item>
                {isAlertActive ? (
                  <div>
                    <Form.Item
                      label="Alert User"
                      name="alertUser"
                      layout="horizontal"
                    >
                      <div className="font-medium text-sm m-1 p-0">
                        {form.getFieldValue("alertUser")}
                      </div>
                    </Form.Item>
                    <Form.Item
                      label="Alert Date"
                      name="alertDate"
                      layout="horizontal"
                      style={{ marginBottom: 0 }}
                      className="-mt-2"
                    >
                      {" "}
                      <div className="font-medium text-sm m-1">
                        {form.getFieldValue("alertDate")
                          ? dayjs(form.getFieldValue("alertDate"))
                            .format("DD MMM YYYY HH:mm")
                            .toUpperCase()
                          : ""}
                      </div>
                    </Form.Item>
                  </div>
                ) : null}
                {isAlertActive ? (
                  <Form.Item
                    label="Alert Note"
                    name="alertNote"
                    rules={[
                      {
                        max: 250,
                        message: "Alert Note must be at most 250 characters.",
                      },
                    ]}
                    className="col-span-2"
                  >
                    <TextArea
                      rows={2}
                      size="large"
                      readOnly={!isAlertActive}
                    // autoSize={true}
                    />
                  </Form.Item>
                ) : null}
              </div>
            </Card>
          </div>
          <div className="lg:w-1/2 flex flex-col gap-2">
            <Card className="pb-1 rounded-3xl shadow-xl bg-white bg-opacity-80 ">
              <div className="grid grid-cols-2 gap-2 items-end">
                <Form.Item name="paymentDueDate" label="Payment Due Date">
                  <CustomDateTimePicker
                    type="date"
                    form={form}
                    itemName="paymentDueDate"
                    className="w-full h-[34px]"
                  />
                </Form.Item>
                <Form.Item
                  label="Payment Sent"
                  name="isPaid"
                  valuePropName="checked"
                >
                  <Switch
                    checked={isPaid}
                    size="large"
                    onChange={(checked) => setIsPaid(checked)}
                  />
                </Form.Item>
                <Form.Item
                  label="Warehouse"
                  name="warehouseId"
                  rules={[
                    { required: true, message: "Please Select Warehouse" },
                  ]}
                  className=""
                >
                  <WarehouseSelect size="medium" onChange={handleWarehouseChange} />
                </Form.Item>
                <Form.Item
                  label="Warehouse Receiving Zone"
                  name="warehouseReceivingZoneId"
                  // rules={[warehouseDeliveryValidator("deliveryDate")]}
                  dependencies={["deliveryDate"]}
                >
                  <WarehouseReceivingZoneSelect
                    size="medium"
                    warehouseId={selectedWarehouseId}
                    onChange={handleReceivingZoneChange}
                  />
                </Form.Item>
                <Form.Item
                  label="Delivery Method"
                  name="containerDeliveryMethodID"
                >
                  <ContainerDeliveryMethodSelect selectClassNames="!h-[34px]" size="large" onChange={(value) => setIsLiveUnload(value === 1)} />
                </Form.Item>
                <Form.Item label="Warehouse Dock"
                  rules={[warehouseDockValidator("warehouseReceivingZoneId", receivingZones)]}
                  dependencies={["warehouseReceivingZoneId"]}
                  name="warehouseDockId">
                  <WarehouseDockSelect
                    size="medium"
                    warehouseId={selectedWarehouseId}
                    receivingZoneId={selectedReceivingZoneId}
                  />
                </Form.Item>
                <Form.Item label="Delivery Date" name="deliveryDate">
                  <CustomDateTimePicker
                    type="datetime"
                    form={form}
                    itemName="deliveryDate"
                    size="large"
                    className="w-full h-[34px]"
                    disabled={isDeliveryConfirmed || !isEditing}
                    onOpenChange={(open) => {
                      if (open && isDeliveryConfirmed && isEditing) {
                        message.destroy();
                        message.warning(
                          <>
                            Container delivery has already been confirmed and
                            can not be rescheduled.
                            <br />
                            To enable rescheduling, please update 'Delivery
                            Confirmed' to 'No'.
                          </>,
                          10,
                        );
                      }
                    }}
                  />
                </Form.Item>
                <div className="flex items-start justify-between xl:flex-row flex-col">
                  <Form.Item
                    label="Delivery Confirmed?"
                    name="isDeliveryConfirmed"
                    valuePropName="checked"
                    labelCol={{ style: { whiteSpace: 'nowrap' } }}
                  >
                    <Switch
                      checked={isDeliveryConfirmed}
                      size="large"
                      onChange={(checked) => setIsDeliveryConfirmed(checked)}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Is On Site?"
                    name="isOnSite"
                    valuePropName="checked"
                    labelCol={{ style: { whiteSpace: 'nowrap' } }}
                  >
                    <Switch
                      checked={isOnSite}
                      size="large"
                      onChange={(checked) => setIsOnSite(checked)}
                      disabled={!form.getFieldValue("isOnSiteEnabled") || form.getFieldValue("containerCollected") || form.getFieldValue("containerReturned")}
                    />
                  </Form.Item>
                </div>
                <Form.Item
                  name="unloadComplete"
                  label="Unload Complete"
                  rules={[
                    {
                      required: false,
                      message: "Please Select Unload Complete Date",
                    },
                  ]}
                >
                  <CustomDateTimePicker
                    type="datetime"
                    form={form}
                    itemName="unloadComplete"
                    size="large"
                    className="w-full h-[34px]"
                  />
                </Form.Item>
                <Form.Item
                  label="Stock Received?"
                  name="isStockReceived"
                  valuePropName="checked"
                >
                  <Switch
                    checked={isStockReceived}
                    size="large"
                    onChange={(checked) => setIsStockReceived(checked)}
                  />
                </Form.Item>
                <Form.Item
                  name="collectionBooked"
                  label="Collection Booked"
                  rules={[
                    {
                      required: false,
                      message: "Please Select Collection Booked Date",
                    },
                  ]}
                >
                  <CustomDateTimePicker
                    disabled={isShipmentLCL || isLiveUnload}
                    type="datetime"
                    form={form}
                    itemName="collectionBooked"
                    size="large"
                    className="w-full h-[34px]"
                  />
                </Form.Item>
                <Form.Item
                  name="containerCollected"
                  label="Container Collected"
                  rules={[
                    {
                      required: false,
                      message: "Please Select Container Collected Date",
                    },
                  ]}
                >
                  <CustomDateTimePicker
                    disabled={isShipmentLCL || isLiveUnload}
                    type="datetime"
                    form={form}
                    itemName="containerCollected"
                    size="large"
                    className="w-full h-[34px]"
                  />
                </Form.Item>
                <Form.Item
                  name="containerReturned"
                  label="Container Returned"
                  rules={[
                    {
                      required: false,
                      message: "Please Select Container Returned Date",
                    },
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <CustomDateTimePicker
                    type="datetime"
                    form={form}
                    itemName="containerReturned"
                    size="large"
                    className="w-full h-[34px]"
                  />
                </Form.Item>
              </div>
            </Card>
            <Card className="pb-1 rounded-3xl shadow-xl bg-white bg-opacity-80 h-full">
              <Flex justify="space-between" align="end" gap={8}>
                <Form.Item
                  label="Boxes"
                  name="boxes"
                  rules={[{ validator: validateNonNegativeNumber(false) }]}
                  labelAlign="left"
                >
                  <InputNumber
                    controls={false}
                    min={0}
                    onKeyDown={(e) => {
                      const allowedKeys = [
                        "Backspace",
                        "Delete",
                        "ArrowLeft",
                        "ArrowRight",
                        "Tab",
                        "Home",
                        "End",
                      ];
                      const isNumberKey = e.key >= "0" && e.key <= "9";
                      const isAllowed =
                        isNumberKey || allowedKeys.includes(e.key);

                      if (!isAllowed) {
                        e.preventDefault();
                      }
                    }}
                  />
                </Form.Item>
                <Form.Item
                  label="Unload Man Hours"
                  name="boxesUnloadManHours"
                  rules={[{ validator: validateNonNegativeNumber(true) }]}
                >
                  <InputNumber
                    controls={false}
                    min={0}
                    precision={2}
                    onKeyDown={(e) => {
                      const allowedKeys = [
                        "Backspace",
                        "Delete",
                        "ArrowLeft",
                        "ArrowRight",
                        "Tab",
                        "Home",
                        "End",
                      ];
                      const isNumberKey = e.key >= "0" && e.key <= "9";
                      if (e.key === ".") {
                        if (e.currentTarget.value.includes(".")) {
                          e.preventDefault();
                        }
                        return;
                      }

                      const isAllowed =
                        isNumberKey || allowedKeys.includes(e.key);

                      if (!isAllowed) {
                        e.preventDefault();
                      }
                    }}
                  />
                </Form.Item>
                <Form.Item
                  name="boxesActualUnloadHour"
                  label="Actual Hours Taken"
                  rules={[{ validator: validateNonNegativeNumber(true) }]}
                  labelAlign="left"
                >
                  <InputNumber
                    controls={false}
                    size="large"
                    min={0}
                    precision={2}
                    onKeyDown={(e) => {
                      const allowedKeys = [
                        "Backspace",
                        "Delete",
                        "ArrowLeft",
                        "ArrowRight",
                        "Tab",
                        "Home",
                        "End",
                      ];
                      const isNumberKey = e.key >= "0" && e.key <= "9";
                      if (e.key === ".") {
                        if (e.currentTarget.value.includes(".")) {
                          e.preventDefault();
                        }
                        return;
                      }

                      const isAllowed =
                        isNumberKey || allowedKeys.includes(e.key);

                      if (!isAllowed) {
                        e.preventDefault();
                      }
                    }}
                  />
                </Form.Item>
              </Flex>
              <Flex justify="space-between" align="end" gap={8}>
                <Form.Item
                  label="Pallets"
                  name="pallets"
                  rules={[{ validator: validateNonNegativeNumber(false) }]}
                >
                  <InputNumber
                    controls={false}
                    min={0}
                    onKeyDown={(e) => {
                      const allowedKeys = [
                        "Backspace",
                        "Delete",
                        "ArrowLeft",
                        "ArrowRight",
                        "Tab",
                        "Home",
                        "End",
                      ];
                      const isNumberKey = e.key >= "0" && e.key <= "9";
                      const isAllowed =
                        isNumberKey || allowedKeys.includes(e.key);

                      if (!isAllowed) {
                        e.preventDefault();
                      }
                    }}
                  />
                </Form.Item>
                <Form.Item
                  label="Unload Man Hours"
                  name="palletsUnloadManHours"
                  rules={[{ validator: validateNonNegativeNumber(true) }]}
                >
                  <InputNumber
                    controls={false}
                    min={0}
                    precision={2}
                    onKeyDown={(e) => {
                      const allowedKeys = [
                        "Backspace",
                        "Delete",
                        "ArrowLeft",
                        "ArrowRight",
                        "Tab",
                        "Home",
                        "End",
                      ];
                      const isNumberKey = e.key >= "0" && e.key <= "9";
                      if (e.key === ".") {
                        if (e.currentTarget.value.includes(".")) {
                          e.preventDefault();
                        }
                        return;
                      }

                      const isAllowed =
                        isNumberKey || allowedKeys.includes(e.key);

                      if (!isAllowed) {
                        e.preventDefault();
                      }
                    }}
                  />
                </Form.Item>
                <Form.Item
                  name="palletsActualUnloadHour"
                  label="Actual Hours Taken"
                  rules={[{ validator: validateNonNegativeNumber(true) }]}
                  labelAlign="left"
                >
                  <InputNumber
                    className="!h-[34px]"
                    controls={false}
                    size="large"
                    min={0}
                    precision={2}
                    onKeyDown={(e) => {
                      const allowedKeys = [
                        "Backspace",
                        "Delete",
                        "ArrowLeft",
                        "ArrowRight",
                        "Tab",
                        "Home",
                        "End",
                      ];
                      const isNumberKey = e.key >= "0" && e.key <= "9";
                      if (e.key === ".") {
                        if (e.currentTarget.value.includes(".")) {
                          e.preventDefault();
                        }
                        return;
                      }

                      const isAllowed =
                        isNumberKey || allowedKeys.includes(e.key);

                      if (!isAllowed) {
                        e.preventDefault();
                      }
                    }}
                  />
                </Form.Item>
              </Flex>
              <Form.Item
                label="Contents"
                name="contents"
                rules={[
                  { required: true, message: "Please input the contents!" },
                  {
                    max: 20,
                    message: "Contents must be at most 20 characters.",
                  },
                ]}
                className="flex-grow"
              >
                <Input size="large" className="" />
              </Form.Item>
              <Form.Item label="Notes" name="notes" className="col-span-2">
                <TextArea rows={2} size="large" className="h-full" />
              </Form.Item>
            </Card>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-start ">
          <div className="grid gap-3 auto-rows-max "></div>
          <div className="grid gap-3"></div>

          <Row gutter={16}>
            <Col span={8}></Col>
            <Col span={4}></Col>
            <Col span={3}></Col>
            <Col span={3}></Col>
            <Col span={3}></Col>
            <Col span={3}></Col>
            {/* <Col span={8}>
              <Form.Item
                name="cargoReadyDate"
                label="CARGO READY DATE"
                rules={[{ required: true, message: "Please Select CRD" }]}
              >
                <DatePicker
                  className="w-full"
                  format="DD MMM YYYY"
                  size="large"
                />
              </Form.Item>
            </Col> */}
          </Row>

          <Row className="flex w-full justify-end mb-2" gutter={16}>
            <Col>
              <AppButton variant="save" onClick={handleSave}>
                Save
              </AppButton>
               <ConfirmDeleteButton title={
                <>
                  {`You are about to delete Shipment for Container #${record?.container?.containerNum}.`}
                  <br />
                  Please confirm you wish to proceed.
                </>
              }
                onConfirm={handleDelete} />
              <AppButton variant="close" onClick={handleClose}>
                Close
              </AppButton>
            </Col>
          </Row>
        </div>
      </Form>
      <Card className="pb-1 rounded-3xl shadow-xl bg-white bg-opacity-80 mt-2">
        <Tabs
          type="card"
          activeKey={activeKey}
          onChange={setActiveKey}
          tabBarExtraContent={
            activeKey === "document" && (
              <AppButton variant="new" onClick={uploadModal}>
            New
          </AppButton>
            )
          }
        >
          <Tabs.TabPane tab="Document" key="document">
            <div className="text-center text-slate-500">
              <Table
                columns={columns}
                dataSource={arranged(record?.documents)}
                pagination={false}
                rowKey="key"
                className="border rounded"
                scroll={{ x: "max-content" }} // or a fixed px like 1000
                rowClassName={(_, index) =>
                  `text-sm ${index % 2 === 0 ? "" : "bg-[#f3f3f3]"}`
                }
                size="middle"
              />
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane tab="Status History" key="status-history">
            <Table
              columns={statusColumns}
              dataSource={record?.shipmentDetailsStatusHistory}
              pagination={false}
              rowKey="id"
              className="border rounded"
              rowClassName={(_, index) =>
                `text-sm ${index % 2 === 0 ? "" : "bg-[#f3f3f3]"}`
              }
              size="middle"
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab="ETA History" key="eta-history">
            <Table
              columns={etaColumns}
              dataSource={record?.shipmentETAHistories}
              pagination={false}
              rowKey="id"
              className="border rounded"
              rowClassName={(_, index) =>
                `text-sm ${index % 2 === 0 ? "" : "bg-[#f3f3f3]"}`
              }
              size="middle"
            />
          </Tabs.TabPane>
        </Tabs>
      </Card>

      <DocumentModal
        isOpen={isModalOpen}
        entityID={containerID}
        onClose={() => handleCloseModal()}
        selectedDocumentTypeID={selectedDocRow?.documentType?.id}
        initialValues={selectedDocRow}
        onSubmit={(values) => {
          setIsModalOpen(false);
        }}
      />
      <DocumentPreviewModal
        isOpen={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        document={selectedDocument}
      />
    </div >
  );
}
const arranged = (arr) => {
  if (!Array.isArray(arr)) return [];

  const filtered = arr
    .filter(
      (doc) =>
        doc.documentType?.isMandatory === true || typeof doc.id === "string",
    )
    .map((doc, index) => ({ ...doc, key: index })); // Added a unique key for each item

  const sorted = filtered.sort(
    (a, b) =>
      (b.documentType?.isMandatory === true) -
      (a.documentType?.isMandatory === true),
  );
  return sorted;
};
